import {
    Button,
    Copy,
    Icon,
    IconLib,
    Label,
    logger,
    Menu,
    MenuDivider,
    MenuHeading,
    MenuItem,
    MenuSection,
    Portal,
    Text,
    Toggle,
    useCopy,
    useDialog,
    useToast,
    View,
} from '@fold-dev/core'
import { useMemo, useState } from 'react'
import { TbArchive, TbCircleFilled, TbSettings, TbShare, TbTrash, TbUserPlus } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { ColorPanel, IconPanel } from '../components'
import { colors, icons, urlContainsId } from '../helpers/util'
import { useProjects } from '../hooks/api.hooks'
import { useAppStore } from '../store/app'

export const ProjectMenu = ({ project, border = 'none', onInvite, onSettings, onDismiss }) => {
    const { workspaceId, projectId } = useParams()
    const { id = '' } = project
    const navigate = useNavigate()
    const actions = useAppStore((state) => state.actions)
    const [icon, setIcon] = useState(project.icon)
    const [color, setColor] = useState(project.color)
    const [archived, setArchived] = useState(project.archived)
    const [isChannel, setIsChannel] = useState(project.isChannel)
    const [joinable, setJoinable] = useState(project.joinable)
    const [roadmap, setRoadmap] = useState(project.roadmap)
    const { setDialog, closeDialog } = useDialog()
    const projectsAPI = useProjects()
    const { copyToClipboard } = useCopy()
    const { showToast } = useToast()
    const workspace = useAppStore((state) => state.workspace)
    //const isAdmin = useMemo(() => workspace.role == 'Owner' || workspace.role == 'Admin', [workspace.role])
    const isAdmin = useMemo(() => workspace.role != 'Guest', [workspace.role])

    const handleCopy = () => {
        const url = window.location.href.split('?')[0]
        copyToClipboard(url)
        showToast({ message: 'URL copied to clipboard.', anchor: 'bottom-right' })
    }

    const handleProjectIconUpdate = async (icon) => {
        actions.projectUpdate({ id, icon })

        setIcon(icon)

        await projectsAPI.update(workspaceId, id, { icon })
    }

    const handleProjectColorUpdate = async (color) => {
        actions.projectUpdate({ id, color })

        setColor(color)

        await projectsAPI.update(workspaceId, id, { color })
    }

    const handleProjectArchivedUpdate = async () => {
        setArchived(!archived)

        actions.projectUpdate({ id, archived: !archived })

        if (archived) {
            actions.projectAdd(project)
        } else {
            actions.projectDelete(id)
        }

        onDismiss()

        await projectsAPI.update(workspaceId, id, { id, archived: !archived })
    }

    const handleProjectRoadmapUpdate = async (e) => {
        try {
            const updatedRoadmap = !roadmap
            actions.projectUpdate({ id, roadmap: updatedRoadmap })
            setRoadmap(updatedRoadmap)
            await projectsAPI.update(workspaceId, id, { roadmap: updatedRoadmap })
        } catch (e) {
            logger(e)
        }
    }

    const handleProjectJoinableUpdate = async (e) => {
        try {
            const updatedJoinable = !joinable
            actions.projectUpdate({ id, joinable: updatedJoinable })
            setJoinable(updatedJoinable)
            await projectsAPI.update(workspaceId, id, { joinable: updatedJoinable })
        } catch (e) {
            logger(e)
        }
    }

    const handleProjectChannelUpdate = async (e) => {
        try {
            const updatedIsChannel = !isChannel
            actions.projectUpdate({ id, isChannel: updatedIsChannel })
            setIsChannel(updatedIsChannel)
            await projectsAPI.update(workspaceId, id, { isChannel: updatedIsChannel })
        } catch (e) {
            logger(e)
        }
    }

    const handleDelete = (e) => {
        setDialog({
            title: 'Are you sure?',
            description: 'This action cannot be undone.',
            portal: Portal,
            footer: (
                <View
                    width="100%"
                    row
                    justifyContent="space-between">
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button
                        onClick={() => {
                            const projectId = project.id
                            actions.projectDelete(projectId)
                            actions.starredDelete(projectId)
                            closeDialog()
                            onDismiss()
                            projectsAPI.remove(workspaceId, project.id)
                            urlContainsId(projectId, () => navigate(`/app/workspace/${workspaceId}/today`))
                        }}
                        variant="danger">
                        Delete
                    </Button>
                </View>
            ),
        })
    }

    return (
        <Menu
            border={border}
            width={230}>
            <MenuHeading>{project.name}</MenuHeading>
            <MenuItem
                disabled={!isAdmin}
                onClick={onSettings}
                prefix={<Icon icon={TbSettings} />}>
                Settings
            </MenuItem>
            <MenuItem
                prefix={<Icon icon={TbShare} />}
                onClick={() => {
                    handleCopy()
                    onDismiss()
                }}>
                Copy link
            </MenuItem>
            <MenuItem
                disabled={!isAdmin}
                prefix={<Icon icon={TbUserPlus} />}
                onClick={onInvite}>
                Add People
            </MenuItem>
            <MenuDivider />
            <MenuItem
                disabled={!isAdmin || project.isPrivate}
                prefix={
                    <Icon
                        icon={TbCircleFilled}
                        style={{ color }}
                    />
                }
                suffix={<IconLib icon="more-h" />}
                menu={
                    <Menu width={200}>
                        <MenuSection p={0}>
                            <ColorPanel
                                colors={colors}
                                color={color}
                                onChange={handleProjectColorUpdate}
                            />
                        </MenuSection>
                    </Menu>
                }>
                Theme
            </MenuItem>
            <MenuItem
                display="none"
                disabled={!isAdmin || project.isPrivate || project.isChannel}
                prefix={<IconLib icon={icon || 'more-h'} />}
                suffix={<IconLib icon="more-h" />}
                menu={
                    <Menu width={220}>
                        <MenuSection p={0}>
                            <IconPanel
                                icon={icon}
                                icons={icons}
                                onChange={handleProjectIconUpdate}
                            />
                        </MenuSection>
                    </Menu>
                }>
                Icon
            </MenuItem>
            <MenuDivider />
            {!project.isPrivate && (
                <>
                    <MenuSection>
                        <View
                            row
                            gap="1rem"
                            justifyContent="flex-start">
                            <Toggle
                                size="sm"
                                id="closed"
                                disabled={!isAdmin}
                                on={joinable}
                                onChange={handleProjectJoinableUpdate}
                            />
                            <View
                                column
                                gap="0.25rem"
                                alignItems="flex-start">
                                <Text
                                    fontWeight={500}
                                    colorToken="text-weak">
                                    Joinable
                                </Text>
                                <Text
                                    size="sm"
                                    fontWeight={500}
                                    colorToken="text-weaker">
                                    External people can join
                                </Text>
                            </View>
                        </View>
                    </MenuSection>
                    {!!joinable && (
                        <MenuSection>
                            <View
                                row
                                gap="1rem"
                                justifyContent="flex-start">
                                <Copy
                                    label={`${window.location.origin}/c/${project.shortcode}`}
                                    value={`${window.location.origin}/c/${project.shortcode}`}
                                    size="sm"
                                />
                            </View>
                        </MenuSection>
                    )}
                    <MenuDivider />
                    <MenuSection>
                        <View
                            row
                            gap="1rem"
                            justifyContent="flex-start">
                            <Toggle
                                size="sm"
                                id="closed"
                                disabled={!isAdmin}
                                on={roadmap}
                                onChange={handleProjectRoadmapUpdate}
                            />
                            <View
                                column
                                gap="0.25rem"
                                alignItems="flex-start">
                                <Text
                                    fontWeight={500}
                                    colorToken="text-weak">
                                    Roadmap
                                </Text>
                                <Text
                                    size="sm"
                                    fontWeight={500}
                                    colorToken="text-weaker">
                                    Public viewable roadmap
                                </Text>
                            </View>
                        </View>
                    </MenuSection>
                    {!!roadmap && (
                        <MenuSection>
                            <View
                                row
                                gap="1rem"
                                justifyContent="flex-start">
                                <Copy
                                    label={`${window.location.origin}/roadmap/${project.shortcode}`}
                                    value={`${window.location.origin}/roadmap/${project.shortcode}`}
                                    size="sm"
                                />
                            </View>
                        </MenuSection>
                    )}
                    <MenuDivider />
                    <MenuSection>
                        <View
                            row
                            gap="1rem"
                            justifyContent="flex-start">
                            <Toggle
                                size="sm"
                                id="closed"
                                disabled={!isAdmin}
                                on={isChannel}
                                onChange={handleProjectChannelUpdate}
                            />
                            <View
                                column
                                gap="0.25rem"
                                alignItems="flex-start">
                                <Text
                                    fontWeight={500}
                                    colorToken="text-weak">
                                    Channel mode
                                </Text>
                                <Text
                                    size="sm"
                                    fontWeight={500}
                                    colorToken="text-weaker">
                                    {isChannel ? 'On' : 'Off'}
                                </Text>
                            </View>
                        </View>
                    </MenuSection>
                    <MenuDivider />
                </>
            )}
            <MenuItem
                disabled={!isAdmin}
                prefix={<Icon icon={TbArchive} />}
                onClick={handleProjectArchivedUpdate}>
                {archived ? 'Unarchive' : 'Archive'}
            </MenuItem>
            <MenuItem
                disabled={!isAdmin}
                prefix={<Icon icon={TbTrash} />}
                onClick={handleDelete}>
                Delete
            </MenuItem>
        </Menu>
    )
}
